import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import WaveDivider from "@/components/WaveDivider";

const tiers = [
  {
    id: "essential",
    name: "Essential",
    cocktails: 4,
    tagline: "Beautifully simple bar service",
    duration: "Up to 3 hours",
    features: ["4 signature cocktails", "Professional bartender", "Full bar setup & tools", "Bottled drinks & cans on request"],
  },
  {
    id: "premium",
    name: "Premium",
    cocktails: 6,
    tagline: "Our most-loved coastal experience",
    duration: "Up to 4 hours",
    featured: true,
    features: ["6 signature cocktails", "Professional bartender", "Full bar setup & glass styling", "Bottled drinks & cans on request", "Menu consultation included"],
  },
  {
    id: "ultimate",
    name: "Ultimate",
    cocktails: 8,
    tagline: "The full luxury bar takeover",
    duration: "Up to 5 hours",
    features: ["8 signature cocktails", "Professional bartender", "Premium bar setup & styling", "Bottled drinks & cans on request", "Bespoke menu design", "Priority peak-season booking"],
  },
];

export const Packages = () => (
  <section id="packages" data-testid="packages-section" className="relative bg-ocean-deep text-white">
    <WaveDivider fill="#0A1F2D" />
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-28">
      <Reveal className="max-w-2xl">
        <p className="overline text-xs text-coral mb-5">Packages</p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
          Three tiers, one dedicated bartender.
        </h2>
        <p className="text-white/70 mt-5 text-base leading-relaxed">
          Pricing scales with your guest count — use the calculator for a live estimate. Every package is delivered personally, start to finish.
        </p>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
        {tiers.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.08}>
            <div
              data-testid={`package-card-${t.id}`}
              className={`h-full flex flex-col p-8 rounded-3xl border transition-transform duration-500 hover:-translate-y-2 ${
                t.featured ? "bg-coral border-coral" : "bg-white/[0.04] border-white/12 hover:border-white/30"
              }`}
            >
              {t.featured && <span className="overline text-[10px] bg-white text-coral rounded-full px-3 py-1 w-fit mb-5">Most Popular</span>}
              <h3 className="font-serif text-4xl mb-1">{t.name}</h3>
              <p className={`text-sm mb-6 ${t.featured ? "text-white/90" : "text-white/60"}`}>{t.tagline}</p>
              <div className="flex items-end gap-2 mb-6">
                <span className="font-serif text-6xl leading-none">{t.cocktails}</span>
                <span className={`text-sm mb-2 ${t.featured ? "text-white/90" : "text-white/60"}`}>signature<br />cocktails</span>
              </div>
              <p className={`overline text-[10px] mb-6 ${t.featured ? "text-white/90" : "text-coral"}`}>{t.duration}</p>
              <ul className="space-y-3 mb-8 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.featured ? "text-white" : "text-coral"}`} />
                    <span className={t.featured ? "text-white/95" : "text-white/80"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/quote?package=${t.id}`}
                data-testid={`package-quote-${t.id}`}
                className={`group flex items-center justify-between rounded-full px-6 py-4 overline text-xs transition-colors ${
                  t.featured ? "bg-ocean-deep text-white hover:bg-black" : "bg-white text-ocean-deep hover:bg-coral hover:text-white"
                }`}
              >
                Get Quote
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
    <WaveDivider fill="#0A1F2D" flip className="bg-cream" />
  </section>
);

export default Packages;
